import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { safeLog, safeError } from '@/utils/safeLogger';
import { supabase, SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useFamily } from '@/contexts/FamilyContext';
import { useSubscription } from './useSubscription';
import { buildGenerationContext } from '@/domain/generation/buildGenerationContext';
import { buildPrompt } from '@/domain/generation/buildPrompt';
import { derivePayloadFromContext } from '@/domain/generation/derivePayloadFromContext';
import type { Family, Profile } from '@/domain/generation/types';
import { checkChatRequestAgainstProfile } from '@/utils/chatBlockedCheck';
import { getAppLocale } from '@/utils/appLocale';
import { resolveChatRecipeServings } from '@/utils/chatRecipeServings';

/** Повтор запроса при сетевой/протокольной ошибке (ERR_HTTP2_PROTOCOL_ERROR, Failed to fetch). */
async function fetchWithRetry(
  url: string,
  options: RequestInit,
  retries = 1
): Promise<Response> {
  try {
    return await fetch(url, options);
  } catch (err) {
    const isNetworkError =
      err instanceof TypeError && (err.message === 'Failed to fetch' || err.message?.includes('fetch')) ||
      (err as Error)?.message?.includes('HTTP2') ||
      (err as Error)?.message?.includes('protocol');
    if (retries > 0 && isNetworkError) {
      await new Promise((r) => setTimeout(r, 1500));
      return fetchWithRetry(url, options, retries - 1);
    }
    throw err;
  }
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

function toProfile(m: {
  id: string;
  name: string;
  age_months?: number | null;
  allergies?: string[];
  type?: string;
  preferences?: string[];
  likes?: string[];
  dislikes?: string[];
}): Profile {
  const role = (m.type === 'adult' || m.type === 'family') ? 'adult' : 'child';
  return {
    id: m.id,
    role,
    name: m.name,
    age: m.age_months != null ? m.age_months / 12 : undefined,
    allergies: m.allergies ?? [],
    preferences: m.preferences ?? [],
    likes: m.likes ?? [],
    dislikes: m.dislikes ?? [],
  };
}

export function useDeepSeekAPI() {
  const { user, session } = useAuth();
  const { members, selectedMember, selectedMemberId } = useFamily();
  const queryClient = useQueryClient();
  const { canGenerate, refetchUsage, subscriptionStatus } = useSubscription();
  const chatAbortRef = useRef<AbortController | null>(null);
  const plan = (subscriptionStatus === 'premium' || subscriptionStatus === 'trial' || subscriptionStatus === 'free')
    ? subscriptionStatus
    : 'free';

  /** Токен для Edge: всегда через refreshSession(), чтобы не отправлять истёкший access_token (getSession() отдаёт кэш, в т.ч. просроченный). */
  const getValidAccessToken = async (): Promise<string> => {
    const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError) {
      safeError('Failed to refresh auth session for AI request:', refreshError);
    }
    const token = refreshedSession?.access_token;
    if (token) return token;

    const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) safeError('Failed to get auth session for AI request:', sessionError);
    const fallbackToken = currentSession?.access_token ?? session?.access_token;
    if (fallbackToken) return fallbackToken;

    throw new Error('Необходима авторизация');
  };

  const abortChat = () => {
    if (chatAbortRef.current) {
      chatAbortRef.current.abort();
    }
  };

  // Chat with DeepSeek (streaming: onChunk вызывается на каждом чанке, в конце возвращается полный message)
  const chatMutation = useMutation({
    mutationFn: async ({
      messages,
      type = 'chat',
      overrideSelectedMemberId,
      overrideSelectedMember,
      overrideMembers,
      onChunk,
      extraSystemSuffix,
      mealType,
      maxCookingTime,
      /** Second client call in the same send (anti-duplicate retry); Edge skips duplicate usage_events. */
      isRetry,
    }: {
      messages: ChatMessage[];
      type?: 'chat' | 'recipe' | 'diet_plan' | 'sos_consultant';
      overrideSelectedMemberId?: string | null;
      overrideSelectedMember?: typeof selectedMember;
      overrideMembers?: Array<{ id: string; name: string; age_months?: number | null; allergies?: string[] }>;
      onChunk?: (chunk: string) => void;
      /** Optional suffix for system prompt (e.g. anti-duplicate hint). */
      extraSystemSuffix?: string;
      /** Optional meal type for recipe prompt (breakfast | lunch | dinner | snack). */
      mealType?: string;
      /** Optional max cooking time in minutes. */
      maxCookingTime?: number;
      isRetry?: boolean;
    }) => {
      if (typeof performance !== 'undefined' && performance.mark) {
        performance.mark('chat_request_start');
      }
      if (!canGenerate) {
        throw new Error('usage_limit_exceeded');
      }

      const lastUserMessage = [...messages].reverse().find((m) => m.role === 'user')?.content ?? '';

      const currentSelectedMemberId = overrideSelectedMemberId ?? selectedMemberId;
      const currentMembers = overrideMembers ?? members;

      await queryClient.refetchQueries({ queryKey: ['members', user?.id] });
      const freshMembers = (queryClient.getQueryData(['members', user?.id]) as typeof members) ?? currentMembers;
      const freshSelectedMember = currentSelectedMemberId && currentSelectedMemberId !== 'family'
        ? (freshMembers.find((c) => c.id === currentSelectedMemberId) ?? overrideSelectedMember ?? null)
        : freshMembers[0] ?? null;

      const activeProfileId: string | 'family' = (currentSelectedMemberId === null || currentSelectedMemberId === 'family')
        ? 'family'
        : currentSelectedMemberId;
      const family: Family = {
        id: 'family',
        profiles: freshMembers.map((c) => toProfile(c)),
        activeProfileId,
      };
      const context = buildGenerationContext(family, family.activeProfileId, plan);
      const { memberData, allMembers, targetIsFamily } = derivePayloadFromContext(context, freshMembers.map((c) => ({
        id: c.id,
        name: c.name,
        age_months: c.age_months,
        type: c.type,
        allergies: c.allergies,
      })));
      const generationContextBlock = buildPrompt(context, freshMembers.map((c) => ({
        id: c.id,
        name: c.name,
        age_months: c.age_months,
        allergies: c.allergies,
        preferences: c.preferences,
        likes: c.likes,
        dislikes: c.dislikes,
      })));

      const isRecipeLike = type === 'chat' || type === 'recipe';
      const servingsForRecipe = isRecipeLike
        ? resolveChatRecipeServings({
            targetIsFamily: activeProfileId === 'family' || targetIsFamily,
            members: freshMembers.map((c) => ({ age_months: c.age_months, type: c.type })),
            mealType: mealType ?? null,
          })
        : undefined;

      safeLog('AI Context Sent:', {
        memberData,
        ageMonths: memberData?.ageMonths,
        targetIsFamily,
        allergies: memberData?.allergies,
        preferences: memberData?.preferences,
        generationContextBlockLength: generationContextBlock?.length ?? 0,
        generationContextBlockPreview: generationContextBlock ? generationContextBlock.slice(0, 300) : '',
      });

      const isHelpMode = type === 'sos_consultant';
      const blockedCheck = !isHelpMode && checkChatRequestAgainstProfile({
        text: lastUserMessage,
        member: memberData ? { name: memberData.name, allergies: memberData.allergies, dislikes: memberData.dislikes } : null,
      });
      if (blockedCheck) {
        return blockedCheck;
      }

      /** Должен быть ≥ бюджета Edge на DeepSeek для `sos_consultant` (заголовки + чтение тела) + запас на DB; иначе клиент обрывает запрос раньше, чем Edge успевает отдать JSON. */
      const HELP_REQUEST_TIMEOUT_MS = 110_000;
      const RECIPE_REQUEST_TIMEOUT_MS = 90_000; // рецепт: не зависать бесконечно после деплоя/сетевых сбоев
      chatAbortRef.current = new AbortController();
      const timeoutMs = isHelpMode ? HELP_REQUEST_TIMEOUT_MS : RECIPE_REQUEST_TIMEOUT_MS;
      const timeoutId = setTimeout(() => chatAbortRef.current?.abort(), timeoutMs);
      const accessToken = await getValidAccessToken();
      if (typeof performance !== 'undefined' && performance.mark) {
        performance.mark('chat_request_sent');
      }
      let response: Response;
      try {
        response = await fetchWithRetry(`${SUPABASE_URL}/functions/v1/deepseek-chat`, {
          method: 'POST',
          signal: chatAbortRef.current.signal,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
            ...(SUPABASE_PUBLISHABLE_KEY && { apikey: SUPABASE_PUBLISHABLE_KEY }),
          },
          body: JSON.stringify({
            messages,
            memberData,
            type,
            stream: !isHelpMode,
            maxRecipes: isHelpMode ? undefined : 1,
            targetIsFamily: activeProfileId === 'family' || targetIsFamily,
            memberId: activeProfileId === 'family' ? 'family' : (currentSelectedMemberId ?? undefined),
            ...((activeProfileId === 'family' || targetIsFamily) && allMembers.length > 0 && { allMembers }),
            ...(generationContextBlock && { generationContextBlock }),
            ...(extraSystemSuffix && extraSystemSuffix.trim() && { extraSystemSuffix: extraSystemSuffix.trim() }),
            ...(mealType && { mealType }),
            ...(maxCookingTime != null && Number.isFinite(maxCookingTime) && { maxCookingTime }),
            ...(servingsForRecipe != null && isRecipeLike && { servings: servingsForRecipe }),
            ...((type === 'chat' || type === 'recipe') && { target_locale: getAppLocale() }),
            ...(isRetry === true && { isRetry: true }),
          }),
        });
        clearTimeout(timeoutId);
        if (typeof performance !== 'undefined' && performance.mark) {
          performance.mark('chat_response_received');
        }
      } catch (err) {
        clearTimeout(timeoutId);
        const msg = (err as Error)?.message ?? '';
        if (err instanceof Error && err.name === 'AbortError') {
          throw new Error(isHelpMode ? 'HELP_TIMEOUT' : 'Запрос занял слишком много времени. Попробуйте ещё раз.');
        }
        if (msg.includes('HTTP2') || msg.includes('protocol') || msg === 'Failed to fetch') {
          throw new Error('Соединение прервано. Проверьте интернет и попробуйте ещё раз.');
        }
        throw err;
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        if (response.status === 429) {
          const code = error?.code ?? error?.error;
          if (
            code === 'PREMIUM_DAILY_LIMIT_REACHED' ||
            error?.error === 'PREMIUM_DAILY_LIMIT_REACHED'
          ) {
            refetchUsage?.();
            const e = new Error('PREMIUM_DAILY_LIMIT_REACHED') as Error & {
              payload?: {
                feature: string;
                limit: number;
                used: number;
                limit_kind?: string;
                subscription_status?: string;
              };
            };
            if (error?.payload) e.payload = error.payload;
            throw e;
          }
          if (code === 'LIMIT_REACHED' || error?.error === 'LIMIT_REACHED') {
            refetchUsage?.();
            const e = new Error('LIMIT_REACHED') as Error & { payload?: { feature: string; limit: number; used: number } };
            if (error?.payload) e.payload = error.payload;
            throw e;
          }
          if (error?.error === 'usage_limit_exceeded') {
            throw new Error('usage_limit_exceeded');
          }
          // Для help любой 429 от бэкенда = лимит исчерпан (тело могло не распарситься)
          if (isHelpMode) {
            refetchUsage?.();
            throw new Error('LIMIT_REACHED');
          }
        }
        const msg = error?.message || error?.error || `HTTP ${response.status}`;
        safeError('deepseek-chat error:', response.status, error);
        throw new Error(typeof msg === 'string' ? msg : 'Ошибка API');
      }

      if (isHelpMode) {
        const data = await response.json().catch(() => ({}));
        await refetchUsage();
        return { message: (data?.message ?? '').trim() || 'Нет ответа.' };
      }

      const contentType = response.headers.get('Content-Type') || '';
      // Редирект/нерелевантность и др. возвращают JSON без стрима — сразу парсим, не читаем как SSE
      if (!contentType.includes('text/event-stream')) {
        const data = await response.json().catch(() => ({}));
        await refetchUsage();
        if (typeof performance !== 'undefined' && performance.mark) {
          performance.mark('chat_recipe_ready');
          try {
            const measure = performance.measure('chat_tap_to_recipe', 'chat_request_start', 'chat_recipe_ready');
            console.warn('[LATENCY_AUDIT] tap → recipe', { chat_tap_to_recipe_ms: Math.round(measure.duration) });
          } catch {
            // ignore if marks were cleared
          }
        }
        return data as { message?: string; recipes?: unknown[]; recipe_id?: string | null; blocked?: boolean; blockedByAllergy?: boolean; blockedByDislike?: boolean; [k: string]: unknown };
      }
      if (response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullContent = '';
        let fullBody = '';
        let buffer = '';
        let eventType = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          fullBody += chunk;
          buffer += chunk;
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim();
              continue;
            }
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              if (data === '[DONE]') continue;
              try {
                const parsed = JSON.parse(data);
                if (eventType === 'done' && (parsed?.recipe_id !== undefined || parsed?.recipes !== undefined || parsed?.blocked === true)) {
                  await refetchUsage();
                  if (typeof performance !== 'undefined' && performance.mark) {
                    performance.mark('chat_recipe_ready');
                    try {
                      const measure = performance.measure('chat_tap_to_recipe', 'chat_request_start', 'chat_recipe_ready');
                      console.warn('[LATENCY_AUDIT] tap → recipe', { chat_tap_to_recipe_ms: Math.round(measure.duration) });
                    } catch {
                      // ignore if marks were cleared
                    }
                  }
                  return {
                    message: typeof parsed.message === 'string' ? parsed.message : fullContent,
                    recipes: Array.isArray(parsed.recipes) ? parsed.recipes : [],
                    recipe_id: parsed.recipe_id ?? null,
                    ...(parsed.blocked !== undefined && { blocked: parsed.blocked }),
                    ...(parsed.blocked_by !== undefined && { blocked_by: parsed.blocked_by }),
                    ...(parsed.profile_name !== undefined && { profile_name: parsed.profile_name }),
                    ...(parsed.blocked_items !== undefined && { blocked_items: parsed.blocked_items }),
                    ...(parsed.matched !== undefined && { matched: parsed.matched }),
                    ...(parsed.suggested_alternatives !== undefined && { suggested_alternatives: parsed.suggested_alternatives }),
                    ...(parsed.original_query !== undefined && { original_query: parsed.original_query }),
                    ...(parsed.intended_dish_hint !== undefined && { intended_dish_hint: parsed.intended_dish_hint }),
                    ...(parsed.blockedByAllergy !== undefined && { blockedByAllergy: parsed.blockedByAllergy }),
                    ...(parsed.blockedByDislike !== undefined && { blockedByDislike: parsed.blockedByDislike }),
                    ...(parsed.route !== undefined && { route: parsed.route }),
                  };
                }
                const content = typeof parsed?.delta === 'string'
                  ? parsed.delta
                  : parsed?.choices?.[0]?.delta?.content;
                if (typeof content === 'string') {
                  fullContent += content;
                  onChunk?.(content);
                }
              } catch {
                // ignore malformed SSE lines
              }
              eventType = '';
            }
          }
        }

        // Если пришёл JSON без стрима (редирект/нерелевантность), а ответ прочитали как стрим — парсим тело
        if (fullContent === '' && fullBody.trim().startsWith('{')) {
          try {
            const parsed = JSON.parse(fullBody.trim());
            if (typeof parsed?.message === 'string' && Array.isArray(parsed?.recipes)) {
              await refetchUsage();
              return {
                message: parsed.message,
                recipes: parsed.recipes,
                recipe_id: parsed.recipe_id ?? null,
                ...(parsed.blocked !== undefined && { blocked: parsed.blocked }),
                ...(parsed.blockedByAllergy !== undefined && { blockedByAllergy: parsed.blockedByAllergy }),
                ...(parsed.blockedByDislike !== undefined && { blockedByDislike: parsed.blockedByDislike }),
                ...(parsed.route !== undefined && { route: parsed.route }),
                ...(parsed.topicKey !== undefined && { topicKey: parsed.topicKey }),
                ...(parsed.topicTitle !== undefined && { topicTitle: parsed.topicTitle }),
                ...(parsed.topicShortTitle !== undefined && { topicShortTitle: parsed.topicShortTitle }),
              };
            }
          } catch {
            // ignore
          }
        }

        await refetchUsage();
        return { message: fullContent };
      }

      const data = await response.json().catch(() => ({}));
      await refetchUsage();
      return {
        message: typeof data?.message === 'string' ? data.message : '',
        recipes: Array.isArray(data?.recipes) ? data.recipes : [],
        recipe_id: data?.recipe_id ?? null,
      };
    },
  });

  // Save chat to history (с контекстом: child_id = null для Семья, иначе id члена). Карусель — по контексту, последние 10.
  const CHAT_HISTORY_LIMIT = 10;
  const saveChatMutation = useMutation({
    mutationFn: async ({
      message,
      response,
      messageType = 'text',
      recipeId,
      childId,
      meta,
    }: {
      message: string;
      response: string;
      messageType?: 'text' | 'image' | 'recipe';
      /** ID рецепта в БД (из Edge function или saveRecipesFromChat) */
      recipeId?: string | null;
      /** Контекст чата: null = Семья, иначе id члена семьи */
      childId?: string | null;
      /** Мета (blocked follow-up и др.); сохраняется в chat_history.meta */
      meta?: Record<string, unknown> | null;
    }) => {
      if (!user) throw new Error('Not authenticated');

      const childIdVal = childId ?? null;

      const { data: inserted, error: insertError } = await supabase
        .from('chat_history')
        .insert({
          user_id: user.id,
          child_id: childIdVal,
          message,
          response,
          message_type: messageType,
          recipe_id: recipeId ?? null,
          ...(meta && Object.keys(meta).length > 0 && { meta }),
        })
        .select('id')
        .single();

      if (insertError) {
        safeError('SYNC ERROR:', insertError.message, insertError.details);
        throw insertError;
      }

      // Карусель по контексту: оставить только последние CHAT_HISTORY_LIMIT записей (неархивных, тот же child_id)
      let trimQ = supabase
        .from('chat_history')
        .select('id')
        .eq('user_id', user.id)
        .is('archived_at', null);
      if (childIdVal == null) trimQ = trimQ.is('child_id', null);
      else trimQ = trimQ.eq('child_id', childIdVal);
      const { data: rows } = await trimQ.order('created_at', { ascending: true });
      if (rows && rows.length > CHAT_HISTORY_LIMIT) {
        const toDelete = rows.slice(0, rows.length - CHAT_HISTORY_LIMIT).map((r) => r.id);
        const { error: deleteError } = await supabase
          .from('chat_history')
          .delete()
          .in('id', toDelete);
        if (deleteError) safeError('SYNC ERROR (trim history):', deleteError.message, deleteError.details);
      }

      return inserted?.id ?? null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat_history'] });
    },
  });

  return {
    chat: chatMutation.mutateAsync,
    abortChat,
    saveChat: saveChatMutation.mutateAsync,
    isChatting: chatMutation.isPending,
    chatError: chatMutation.error,
  };
}
